import {
  Button,
  ButtonGroup,
  FormLayout,
  Heading,
  InlineStack,
  Panel,
  PanelInset,
  Paragraph,
  Stack,
  Table,
  TableTbody,
  TableTh,
  TableThead,
  TableTr,
  Text,
  TextField,
} from '@customerio/pluma-components/react';
import { ViewerRestrictedTooltip } from '@/components/overlays/viewer-restricted-tooltip';
import type { AppEditorController } from '../hooks/use-app-editor';
import { PlatformTableRow } from './platform-table-row';

const NAME_DESCRIPTION =
  'Shown to teammates in this workspace. Each platform below has its own app identifier, which is what matches this app to your devices.';

const FIRST_APP_NOTE = "As the first app in this workspace, it'll automatically become the default.";

export type AppEditorProps = {
  editor: AppEditorController;
};

/**
 * The fields lock while a save is in flight: the sequence has already captured
 * what it is going to write, so letting the form move on would show something
 * other than what is being saved.
 */
export function AppEditor({ editor }: AppEditorProps) {
  const { isSaving } = editor;

  return (
    <Stack gap="300">
      <Stack gap="200" inert={isSaving || undefined}>
        <FormLayout>
          <TextField
            label="App name"
            id="app_name"
            value={editor.name}
            isDisabled={!editor.canSaveForm}
            placeholder="e.g. Customer.io shop"
            description={NAME_DESCRIPTION}
            onInput={(event) => editor.setName(event.target.value)}
          />

          {editor.mustBeDefault ? <Paragraph variant="secondary">{FIRST_APP_NOTE}</Paragraph> : null}
        </FormLayout>
      </Stack>

      <Stack gap="200" inert={isSaving || undefined}>
        <Stack gap="050">
          <Heading level="3">Platforms</Heading>
          <Paragraph variant="secondary">
            Select which platforms this app runs on and configure delivery credentials.
          </Paragraph>
        </Stack>

        <Panel>
          <PanelInset>
            <Table caption="Platforms">
              <TableThead>
                <TableTr>
                  <TableTh>Platform</TableTh>
                  <TableTh>App identifier</TableTh>
                  <TableTh textAlign="right">Status</TableTh>
                  <TableTh />
                </TableTr>
              </TableThead>
              <TableTbody>
                {editor.rows.map((row) => (
                  <PlatformTableRow
                    key={row.platform}
                    row={row}
                    canSaveForm={editor.canSaveForm}
                    onEnable={editor.enablePlatform}
                    onEdit={editor.editPlatform}
                    onDisable={editor.disablePlatform}
                  />
                ))}
              </TableTbody>
            </Table>
          </PanelInset>
        </Panel>
      </Stack>

      <Stack gap="200">
        {editor.error == null ? null : <Text color="critical">{editor.error}</Text>}
        <InlineStack alignInline="end" alignBlock="center">
          <ButtonGroup groupVariant="spaced">
            <Button variant="tertiary" isDisabled={isSaving} onClick={() => void editor.requestClose()}>
              Cancel
            </Button>
            <ViewerRestrictedTooltip readOnly={!editor.canSaveForm}>
              {(context) => (
                <Button
                  ref={context.reference.setReference}
                  {...context.reference.getReferenceProps()}
                  variant="primary"
                  isDisabled={!editor.canSave}
                  isLoading={isSaving}
                  onClick={() => void editor.save()}
                >
                  Save
                </Button>
              )}
            </ViewerRestrictedTooltip>
          </ButtonGroup>
        </InlineStack>
      </Stack>
    </Stack>
  );
}
